import type { Node, Edge } from '../../../core/models/scene';
import type {
  ScenarioDelta,
  NodeAddition,
  EdgeAddition,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import { buildStatusIndex } from '../../../core/data-profiles/scenarios/ScenarioModel';
import { getShapeManifest } from '../../../lib/diagram/registries';
import { getRelationshipMapping } from '../../../lib/diagram/registries';

const resolveDefaultDimensions = (shapeType: string): { width: number; height: number } => {
  const manifest = getShapeManifest('flowchart', shapeType);
  return manifest?.defaultDimensions || { width: 120, height: 80 };
};

const additionToNode = (addition: NodeAddition): Node => {
  const dims = resolveDefaultDimensions(addition.type);
  const label = addition.name || addition.title || 'Process';
  const attrs: Record<string, unknown> = {
    label,
    ...(addition.props ?? {}),
  };

  return {
    id: addition.id as string,
    type: addition.type,
    category: 'flowchart',
    x: addition.position?.x ?? 0,
    y: addition.position?.y ?? 0,
    w: dims.width,
    h: dims.height,
    width: dims.width,
    height: dims.height,
    text: label,
    attrs,
    props: {
      ...addition.props,
    },
  } as Node;
};

const additionToEdge = (addition: EdgeAddition): Edge => {
  const mapping = getRelationshipMapping('flowchart', addition.type);
  const edgeType = (mapping?.edgeType ?? addition.type) as Edge['type'];
  const defaultEdgeProps = (mapping?.defaultEdgeProps ?? {}) as Record<string, unknown>;

  return {
    id: addition.id as string,
    type: edgeType,
    from: addition.from as string,
    to: addition.to as string,
    attrs: {
      ...defaultEdgeProps,
      ...(addition.props ?? {}),
    },
  } as Edge;
};

export const projectProcessScenario = (
  baseNodes: Node[],
  baseEdges: Edge[],
  delta: ScenarioDelta<any, any>
): { nodes: Node[]; edges: Edge[]; statusIndex: ReturnType<typeof buildStatusIndex> } => {
  if (
    delta.additions.length === 0 &&
    delta.removals.length === 0 &&
    delta.moves.length === 0 &&
    delta.updates.length === 0
  ) {
    return {
      nodes: baseNodes,
      edges: baseEdges,
      statusIndex: buildStatusIndex(delta),
    };
  }

  const removalIds = new Set(delta.removals.map((r) => r.nodeId as string));
  const edgeRemovalIds = new Set(delta.edgeRemovals.map((r) => r.edgeId as string));
  const updateIndex = new Map(delta.updates.map((u) => [u.nodeId as string, u]));

  const projectedNodes = baseNodes
    .filter((n) => !removalIds.has(n.id))
    .map((n) => {
      const update = updateIndex.get(n.id);
      if (!update) return n;

      const { field, newValue } = update.change as { field: string; newValue: unknown };
      const mappedField = field === 'name' ? 'label' : field;
      const nextText = mappedField === 'label' ? String(newValue ?? '') : n.text;

      return {
        ...n,
        [mappedField]: newValue,
        text: nextText,
        attrs: {
          ...(n.attrs as Record<string, unknown>),
          [mappedField]: newValue,
        },
        props: {
          ...(n.props as Record<string, unknown>),
          [field]: newValue,
        },
      } as Node;
    })
    .concat(delta.additions.map(additionToNode));

  const projectedEdges = baseEdges
    .filter((e) => !removalIds.has(e.from) && !removalIds.has(e.to))
    .filter((e) => !edgeRemovalIds.has(e.id))
    .concat(delta.edgeAdditions.map(additionToEdge));

  return {
    nodes: projectedNodes,
    edges: projectedEdges,
    statusIndex: buildStatusIndex(delta),
  };
};
